import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Calculator, Lock, Pencil, RotateCcw } from 'lucide-react';
import { memo, useCallback, useState } from 'react';
function formatValue(value, paramType) {
    if (value == null) return '—';
    switch(paramType){
        case 'currency':
            if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
            if (value >= 1000) return `$${value.toLocaleString(undefined, {
                maximumFractionDigits: 0
            })}`;
            return `$${value.toFixed(2)}`;
        case 'percentage':
            return `${(value * 100).toFixed(1)}%`;
        case 'rate':
            return `$${value.toFixed(2)}`;
        case 'count':
            return value.toFixed(0);
        case 'ratio':
            return value.toFixed(3);
        default:
            return value.toLocaleString(undefined, {
                maximumFractionDigits: 2
            });
    }
}
export const ParameterCell = /*#__PURE__*/ memo(function ParameterCell({ param, editable, onChange, onOverride, period }) {
    const currentValue = period ? param.periodValues?.[period] ?? null : param.value;
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [showOverridePrompt, setShowOverridePrompt] = useState(false);
    const [overrideReason, setOverrideReason] = useState('');
    const isLocked = param.source === 'government' || param.metadata?.locked;
    const isEvaluation = param.source === 'evaluation';
    const isComputed = param.source === 'computed';
    const isOverridden = !!param.metadata?.override;
    const canEdit = editable && !isLocked && !isComputed && !isEvaluation;
    const handleStartEdit = useCallback(()=>{
        if (!canEdit) return;
        setEditValue(currentValue != null ? String(currentValue) : '');
        setIsEditing(true);
    }, [
        canEdit,
        currentValue
    ]);
    const handleBlur = useCallback(()=>{
        setIsEditing(false);
        const parsed = editValue === '' ? null : Number(editValue);
        if (parsed !== null && Number.isNaN(parsed)) return;
        // Validate constraints
        if (parsed !== null && param.constraints) {
            const { min, max, nte } = param.constraints;
            if (min !== undefined && parsed < min) return;
            if (max !== undefined && parsed > max) return;
            if (nte !== undefined && parsed > nte) return;
        }
        if (parsed !== currentValue) {
            onChange(parsed);
        }
    }, [
        editValue,
        currentValue,
        param.constraints,
        onChange
    ]);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') {
            e.target.blur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }, []);
    const handleOverride = useCallback(()=>{
        if (!onOverride || !overrideReason.trim()) return;
        const overriddenParam = {
            ...param,
            source: 'contractor',
            metadata: {
                ...param.metadata,
                override: {
                    originalValue: currentValue ?? 0,
                    originalSource: param.source,
                    reason: overrideReason.trim(),
                    overriddenAt: new Date().toISOString()
                }
            }
        };
        onOverride(overriddenParam);
        setShowOverridePrompt(false);
        setOverrideReason('');
    }, [
        onOverride,
        overrideReason,
        param,
        currentValue
    ]);
    const handleRevert = useCallback(()=>{
        if (!onOverride || !param.metadata?.override) return;
        const revertedParam = {
            ...param,
            source: param.metadata.override.originalSource,
            value: param.metadata.override.originalValue,
            metadata: {
                ...param.metadata,
                override: undefined
            }
        };
        onOverride(revertedParam);
    }, [
        onOverride,
        param
    ]);
    // Override reason prompt
    if (showOverridePrompt) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-1",
            children: [
                /*#__PURE__*/ _jsx("input", {
                    type: "text",
                    value: overrideReason,
                    onChange: (e)=>setOverrideReason(e.target.value),
                    onKeyDown: (e)=>{
                        if (e.key === 'Enter') handleOverride();
                        else if (e.key === 'Escape') setShowOverridePrompt(false);
                    },
                    placeholder: "Override reason...",
                    className: "w-full px-2 py-1 text-xs border border-orange-400 rounded focus:outline-none focus:ring-1 focus:ring-orange-500",
                    ref: (el)=>el?.focus()
                }),
                /*#__PURE__*/ _jsx("button", {
                    onClick: handleOverride,
                    disabled: !overrideReason.trim(),
                    className: "text-xs text-orange-600 hover:text-orange-700 disabled:opacity-50 px-1",
                    children: "OK"
                })
            ]
        });
    }
    // Editing state
    if (isEditing) {
        return /*#__PURE__*/ _jsx("input", {
            type: "number",
            value: editValue,
            onChange: (e)=>setEditValue(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            className: "w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right",
            step: param.constraints?.noFractions ? 1 : 'any'
        });
    }
    // Read-only display
    const displayValue = formatValue(currentValue, param.paramType);
    // Style based on source
    let cellClass = 'px-2 py-1.5 text-sm text-right rounded relative group/cell ';
    if (isOverridden) {
        cellClass += 'bg-orange-50 text-orange-700';
    } else if (isLocked) {
        cellClass += 'bg-blue-50 text-blue-700';
    } else if (isEvaluation) {
        cellClass += 'bg-violet-50 text-violet-700';
    } else if (isComputed) {
        cellClass += 'bg-gray-50 text-gray-600';
    } else if (canEdit) {
        cellClass += 'cursor-pointer hover:bg-gray-50';
    }
    // Build tooltip
    let tooltipText;
    if (isOverridden && param.metadata?.override) {
        const ov = param.metadata.override;
        tooltipText = `Overridden: was ${formatValue(ov.originalValue, param.paramType)} (${ov.originalSource}). Reason: ${ov.reason}`;
    } else if (isLocked) {
        tooltipText = 'Government-defined (locked)';
    } else if (isEvaluation) {
        tooltipText = 'Government estimate (for evaluation purposes only)';
    } else if (isComputed && param.formula) {
        tooltipText = `Computed: ${param.formula.type}`;
    } else {
        tooltipText = param.metadata?.notes || undefined;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cellClass,
        onClick: handleStartEdit,
        title: tooltipText,
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex items-center gap-1",
                children: [
                    displayValue,
                    isOverridden && /*#__PURE__*/ _jsx(RotateCcw, {
                        className: "w-3 h-3 text-orange-400 inline"
                    }),
                    !isOverridden && isLocked && /*#__PURE__*/ _jsx(Lock, {
                        className: "w-3 h-3 text-blue-400 inline"
                    }),
                    !isOverridden && isEvaluation && /*#__PURE__*/ _jsx(Lock, {
                        className: "w-3 h-3 text-violet-400 inline"
                    }),
                    isComputed && !isOverridden && /*#__PURE__*/ _jsx(Calculator, {
                        className: "w-3 h-3 text-gray-400 inline"
                    })
                ]
            }),
            onOverride && (isLocked || isEvaluation || isComputed) && !isOverridden && /*#__PURE__*/ _jsx("button", {
                onClick: (e)=>{
                    e.stopPropagation();
                    setShowOverridePrompt(true);
                },
                className: "absolute top-0.5 right-0.5 opacity-0 group-hover/cell:opacity-100 transition-opacity p-0.5 rounded bg-white shadow-sm border border-gray-200",
                title: "Override this value",
                children: /*#__PURE__*/ _jsx(Pencil, {
                    className: "w-2.5 h-2.5 text-orange-500"
                })
            }),
            onOverride && isOverridden && /*#__PURE__*/ _jsx("button", {
                onClick: (e)=>{
                    e.stopPropagation();
                    handleRevert();
                },
                className: "absolute top-0.5 right-0.5 opacity-0 group-hover/cell:opacity-100 transition-opacity p-0.5 rounded bg-white shadow-sm border border-gray-200",
                title: "Revert to original value",
                children: /*#__PURE__*/ _jsx(RotateCcw, {
                    className: "w-2.5 h-2.5 text-orange-500"
                })
            })
        ]
    });
});
